import type {
  HttpTransportPort,
  HttpTransportRequestOptions,
  HttpTransportResponse,
} from '@core/domain/ports/driven/HttpTransportPort.ts';

export class XhrHttpAdapter implements HttpTransportPort {
  request(url: string, options: HttpTransportRequestOptions): Promise<HttpTransportResponse> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method, url, true);

      if (options.timeoutMs) {
        xhr.timeout = options.timeoutMs;
      }

      if (options.headers) {
        for (const [key, value] of Object.entries(options.headers)) {
          xhr.setRequestHeader(key, value);
        }
      }

      if (options.onProgress) {
        const reportProgress = (loaded: number, total: number, lengthComputable: boolean): void => {
          if (lengthComputable && total > 0) {
            options.onProgress!(loaded, total);
            return;
          }
          if (loaded > 0) {
            options.onProgress!(loaded, 0);
          }
        };
        if (options.method === 'POST' || options.method === 'PUT') {
          xhr.upload.onprogress = (event) => {
            reportProgress(event.loaded, event.total, event.lengthComputable);
          };
        } else {
          xhr.onprogress = (event) => {
            reportProgress(event.loaded, event.total, event.lengthComputable);
          };
        }
      }

      if (options.signal) {
        options.signal.addEventListener('abort', () => {
          xhr.abort();
          reject(
            typeof DOMException !== 'undefined'
              ? new DOMException('Aborted', 'AbortError')
              : Object.assign(new Error('Aborted'), { name: 'AbortError' }),
          );
        });
      }

      xhr.onload = () => {
        resolve({
          status: xhr.status,
          ok: xhr.status >= 200 && xhr.status < 300,
          getHeader: (name: string) => xhr.getResponseHeader(name),
          text: async () => xhr.responseText,
          json: async () => JSON.parse(xhr.responseText),
        });
      };

      xhr.onerror = () => {
        reject(new TypeError('Failed to fetch')); // Simulate fetch error for compatibility
      };

      xhr.ontimeout = () => {
        reject(
          typeof DOMException !== 'undefined'
            ? new DOMException('The operation timed out.', 'TimeoutError')
            : Object.assign(new Error('The operation timed out.'), { name: 'TimeoutError' }),
        );
      };

      xhr.send((options.body as any) || null);
    });
  }
}
